import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { useSubscriptionStore } from 'state/store/subscription';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useGetSubscriptionPlans, useCancelSubscription, useGetCurrentSubscription } from 'features/subscription/hooks/use-subscription';
import { buildSubscriptionTabView } from './subscription-tab.utils';
import { billingCycleLabel } from 'features/subscription/components/subscription-plan';
import { isIncompleteCheckout } from 'features/subscription/utils/resolve-plan-select-action';

interface SubscriptionTabV2Props {
    tabId: string;
    onCloseModal?: () => void;
}

const GRANTING_STATUSES = new Set(['active', 'trialing', 'pastdue']);

export const SubscriptionTabV2 = ({ onCloseModal }: SubscriptionTabV2Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isCancelOpen, setIsCancelOpen] = useState(false);

    const subscriptionDetails = useSubscriptionStore((state) => state.subscriptionDetails);
    const isSubscriptionLoading = useSubscriptionStore((state) => state.isLoading);
    const isOnlyOrgUser = useCommonStore((state) => state.isOnlyOrgUser);
    useGetCurrentSubscription();
    const { data: plansResponse, isLoading: isPlansLoading } = useGetSubscriptionPlans();
    const { mutateAsync: cancelSubscription, isPending: isCancelling } = useCancelSubscription();

    const view = useMemo(
        () =>
            buildSubscriptionTabView({
                subscriptionDetails,
                plans: plansResponse?.data ?? [],
            }),
        [subscriptionDetails, plansResponse?.data]
    );

    const goToPlans = () => {
        onCloseModal?.();
        navigate('/subscription-plans');
    };

    const continuePayment = () => {
        const checkoutUrl = subscriptionDetails?.checkoutUrl;
        if (!checkoutUrl) return;
        onCloseModal?.();
        window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    };

    const showCancel = !isOnlyOrgUser && view.hasSubscription;
    const planName = view.hasSubscription ? view.planName : t('NO_PLAN', 'No plan');
    const statusKey = view.statusLabel.toLowerCase();
    const statusLabel =
        statusKey === 'active'
            ? t('ACTIVE', 'Active')
            : statusKey === 'inactive'
                ? t('INACTIVE', 'Inactive')
                : view.statusLabel;
    const billingCycle = billingCycleLabel(view.billingCycle, t);
    const hasValidSubscription =
        view.hasSubscription &&
        (GRANTING_STATUSES.has(statusKey) || !statusKey);
    const showChangePlan = !isOnlyOrgUser && hasValidSubscription;
    const isPendingCheckout = isIncompleteCheckout(subscriptionDetails?.status);

    const handleConfirmCancel = async () => {
        const subscriptionId = subscriptionDetails?.subscriptionId;
        if (!subscriptionId) return;
        await cancelSubscription(subscriptionId);
    };

    const cancelDescription = isPendingCheckout ? (
        <>
            {t('UNSUBSCRIBE_PLAN_CONFIRM_PREFIX', 'Are you sure to unsubscribe the')}{' '}
            <strong className="font-bold text-foreground">{planName}</strong>
            {t('UNSUBSCRIBE_PLAN_CONFIRM_SUFFIX', '?')}
        </>
    ) : (
        <>
            {t(
                'CANCEL_SUBSCRIPTION_CONFIRM',
                'Are you sure you want to cancel your subscription? You will keep access until the end of your current billing period.'
            )}
        </>
    );

    if (isSubscriptionLoading || isPlansLoading) {
        return (
            <div className="h-full overflow-auto bg-background">
                <div className="max-w-4xl animate-pulse space-y-4 px-4 py-4 sm:px-6 sm:py-5 md:px-8">
                    <div className="h-6 w-40 rounded bg-muted" />
                    <div className="h-4 w-64 rounded bg-muted" />
                    <div className="h-16 w-full rounded bg-muted" />
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-auto bg-background">
            <div className="max-w-4xl space-y-6 px-4 py-4 sm:space-y-8 sm:px-6 sm:py-5 md:px-8">
                <div>
                    <h2 className="mb-1 text-lg font-semibold text-foreground sm:text-xl">
                        {t('SUBSCRIPTION', 'Subscription')}
                    </h2>
                    <p className="text-xs text-muted-foreground sm:text-sm">
                        {t('SUBSCRIPTION_TAB_DESCRIPTION', 'Manage your current plan, billing, and cancellation.')}
                    </p>
                </div>

                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 space-y-1">
                        <p className="text-sm font-medium text-muted-foreground sm:text-base">
                            {t('CURRENT_PLAN', 'Current plan')}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-semibold text-foreground sm:text-base">
                                {planName}
                            </h3>
                            <span
                                className={
                                    isPendingCheckout
                                        ? 'inline-flex shrink-0 items-center rounded-full border border-warning/30 bg-warning-background px-2 py-0.5 text-[10px] font-semibold text-warning-high-emphasis dark:border-warning/40'
                                        : view.hasSubscription
                                            ? 'inline-flex shrink-0 items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300'
                                            : 'inline-flex shrink-0 items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground'
                                }
                            >
                                {statusLabel}
                            </span>
                        </div>

                        {view.amountLabel !== '-' && (
                            <div className="flex items-baseline gap-1 text-xs sm:text-sm">
                                <span className="font-semibold text-foreground">
                                    {view.amountLabel}
                                </span>
                                <span className="text-muted-foreground">
                                    / {billingCycle}
                                </span>
                            </div>
                        )}
                        {view.nextRenewalLabel && !isPendingCheckout && (
                            <p className="text-xs text-muted-foreground sm:text-sm">
                                {t('SUBSCRIPTION_AUTO_RENEW', {
                                    date: view.nextRenewalLabel,
                                    defaultValue: 'Your subscription will auto renew on {{date}}.',
                                })}
                            </p>
                        )}
                    </div>
                    {isPendingCheckout && (
                        <Button
                            className="shrink-0"
                            onClick={continuePayment}
                            disabled={!subscriptionDetails?.checkoutUrl}
                        >
                            {t('CONTINUE_PAYMENT', 'Continue payment')}
                        </Button>
                    )}
                    {!isPendingCheckout && showChangePlan && (
                        <Button className="shrink-0" onClick={goToPlans}>
                            {t('CHANGE_PLAN', 'Change plan')}
                        </Button>
                    )}
                </div>

                {showCancel && (
                    <>
                        <Separator className="h-px bg-border" />
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1 space-y-1">
                                <h3 className="text-sm font-semibold text-foreground sm:text-base">
                                    {t('CANCEL_SUBSCRIPTION', 'Cancel subscription')}
                                </h3>
                                <p className="text-xs text-muted-foreground sm:text-sm">
                                    {t(
                                        'CANCEL_SUBSCRIPTION_ACCESS_UNTIL_PERIOD_END',
                                        'Once canceled, your plan continues until the end of the current billing period.'
                                    )}
                                </p>
                            </div>
                            <Button
                                variant="ghost"
                                className="shrink-0 font-semibold text-destructive hover:bg-transparent hover:text-destructive/80"
                                onClick={() => setIsCancelOpen(true)}
                            >
                                {t('CANCEL', 'Cancel')}
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <ConfirmationModal
                open={isCancelOpen}
                onOpenChange={setIsCancelOpen}
                nested
                bgBlurred
                confirmVariant="delete"
                title={t('CANCEL_SUBSCRIPTION', 'Cancel Subscription')}
                description={cancelDescription}
                confirmText={t('CANCEL_SUBSCRIPTION', 'Cancel Subscription')}
                cancelText={t('KEEP_SUBSCRIPTION', 'Keep Subscription')}
                isLoading={isCancelling}
                onConfirm={handleConfirmCancel}
            />
        </div>
    );
};
